import { DOCUMENT } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';

import { Observable, of, throwError } from 'rxjs';
import { catchError, finalize, map, share, take, tap } from 'rxjs/operators';

@Injectable({
  providedIn: 'root',
})
export class IconService {
  private readonly iconPath: string = 'assets/icons/';
  private document: Document;
  private chachedIconsSvg = new Map<string, SVGElement>();
  private fetchesInProgress = new Map<string, Observable<string>>();

  constructor(private httpClient: HttpClient, @Inject(DOCUMENT) document: any) {
    this.document = document;
  }

  loadSvg(iconName: string): Observable<SVGElement> {
    if (this.chachedIconsSvg.has(iconName)) {
      const svgElement = this.cloneSvg(this.chachedIconsSvg.get(iconName));

      return of(svgElement);
    }

    return this.fetchUrl(`${this.iconPath}${iconName}.svg`).pipe(
      map((svgContent) => this.svgElementFromString(svgContent)),
      tap((svgElement) => this.chachedIconsSvg.set(iconName, svgElement)),
      catchError(() => throwError(`Unable to find icon with the name "${iconName}`)),
    );
  }

  private fetchUrl(url: string): Observable<string> {
    const isFetchInProgress = this.fetchesInProgress.get(url);

    if (isFetchInProgress) {
      return isFetchInProgress;
    }

    const req = this.httpClient.get(url, { responseType: 'text' }).pipe(
      take(1),
      finalize(() => this.fetchesInProgress.delete(url)),
      share(),
    );

    this.fetchesInProgress.set(url, req);

    return req;
  }

  private svgElementFromString(str: string): SVGElement {
    const div = this.document.createElement('DIV');

    div.innerHTML = str;
    const svg = div.querySelector('svg');

    if (!svg) {
      throw Error('<svg> tag not found');
    }

    return svg;
  }

  private cloneSvg(svg: SVGElement): SVGElement {
    return svg.cloneNode(true) as SVGElement;
  }
}
